import { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import QRCodeStyling from 'qr-code-styling';
import {
  QrCode, Download, FileImage, FileCode, Save, Trash2, RotateCcw, X,
  AlertTriangle, Loader2, Link2, Sparkles, Copy, Eye, EyeOff,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Brand-coded shapes ───────────────────────────────────────────
// The Relative Gallery silhouette — a clean house outline traced
// from the logo, normalized to the 0-100 unit space so we can scale
// it cleanly into any QR canvas (preview, SVG export, PNG export).
// Kept symmetric on purpose: at QR-sticker sizes a symmetric house
// reads as "house" instantly; the wordmark's slight asymmetry only
// works when the typography is present.
const BARN_PATH = 'M 14 93 L 14 27 L 50 6 L 86 27 L 86 93 Z';

// Simplified TRG mark used as the centered logo when the curator
// enables it. Inlined as a data URL so we never depend on a hosted
// asset (no CORS, no 404s, works offline). The white square keeps the
// QR dots immediately around the mark legible.
const HOUSE_MARK_SVG = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" shape-rendering="geometricPrecision">
  <rect width="100" height="100" fill="#FFFFFF"/>
  <path d="M 22 84 L 22 44 L 50 24 L 78 44 L 78 84 Z" fill="none" stroke="#000000" stroke-width="7" stroke-linejoin="miter" stroke-linecap="butt"/>
</svg>`;
const HOUSE_MARK_DATA_URL = `data:image/svg+xml;base64,${typeof window !== 'undefined' ? window.btoa(HOUSE_MARK_SVG) : ''}`;

// ─── Presets ───────────────────────────────────────────────────────
// All four presets are now PURE BLACK AND WHITE — no cyan, no gray,
// no cream — to match the TRG architectural identity. Keys are
// preserved so any QR already saved in the DB still resolves; only
// the rendered look + labels change.
//
// Channels we use to convey "TRG-ness" without harming scannability:
//   1. Module shape  (square / rounded — kept conservative)
//   2. Finder marker shape (architectural square or refined extra-rounded)
//   3. Optional barn-outline frame around the QR (decorative — sits
//      OUTSIDE the QR quiet zone, doesn't touch a single module)
//   4. Optional centered TRG house mark (with Error Correction H)
const PRESETS = {
  'minimal-print': {
    label: 'Minimal',
    helper: 'Pure black on white. Architectural square modules. The TRG default.',
    framed: false,
    inverted: false,
    dotsType: 'square',
    cornersSquareType: 'square',
    cornersDotType: 'square',
  },
  'gallery-cyan': {
    label: 'Architectural',
    helper: 'Softened modules with refined corner markers. Premium, gallery-grade.',
    framed: false,
    inverted: false,
    dotsType: 'rounded',
    cornersSquareType: 'extra-rounded',
    cornersDotType: 'dot',
  },
  'dark-poster': {
    label: 'Inverted',
    helper: 'White modules on pure black. For dark posters & event signage.',
    framed: false,
    inverted: true,
    dotsType: 'rounded',
    cornersSquareType: 'extra-rounded',
    cornersDotType: 'dot',
  },
  'invitation': {
    label: 'Framed',
    helper: 'Wrapped in The Relative Gallery house silhouette. The hallmark.',
    framed: true,
    inverted: false,
    dotsType: 'square',
    cornersSquareType: 'square',
    cornersDotType: 'square',
  },
};

function buildQrOptions({ data, preset, withLogo, size = 1024 }) {
  const p = PRESETS[preset] || PRESETS['minimal-print'];
  const fg = p.inverted ? '#FFFFFF' : '#000000';
  const bg = p.inverted ? '#000000' : '#FFFFFF';
  return {
    width: size,
    height: size,
    type: 'svg',
    data: data || ' ',
    margin: 16,
    // High EC always when a logo is centered (a chunk of the QR is
    // occluded). Q is plenty for the bare QR (~25% recoverable).
    qrOptions: { errorCorrectionLevel: withLogo ? 'H' : 'Q' },
    backgroundOptions: { color: bg },
    dotsOptions: { type: p.dotsType, color: fg },
    cornersSquareOptions: { type: p.cornersSquareType, color: fg },
    cornersDotOptions: { type: p.cornersDotType, color: fg },
    image: withLogo ? HOUSE_MARK_DATA_URL : undefined,
    imageOptions: {
      hideBackgroundDots: true,
      imageSize: 0.20, // slightly smaller than before → safer scan
      margin: 6,
      crossOrigin: 'anonymous',
    },
  };
}

function isProbablyUrl(value) {
  if (!value) return false;
  const v = value.trim();
  if (/^https?:\/\//i.test(v)) {
    try { new URL(v); return true; } catch { return false; }
  }
  // bare hostname / path
  if (/^[a-z0-9-]+\.[a-z]{2,}/i.test(v)) return true;
  if (v.startsWith('mailto:') || v.startsWith('tel:')) return v.length > 7;
  return false;
}

// ─── Branded confirmation modal ────────────────────────────────────
function ConfirmModal({ open, title, body, confirmLabel, busy, onConfirm, onCancel }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          onClick={onCancel}
          data-testid="qr-confirm-modal"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.97, opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.2, 0.7, 0.2, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[440px] bg-[#070707] border border-[#1a1a1a]"
          >
            <div className="p-5 flex items-start gap-3 border-b border-[#1a1a1a]">
              <div className="w-9 h-9 border border-[rgba(232,139,160,0.45)] bg-[rgba(232,139,160,0.06)] flex items-center justify-center shrink-0">
                <AlertTriangle className="w-4 h-4 text-[#e88ba0]" />
              </div>
              <div className="min-w-0">
                <h3 className="text-[13px] font-outfit text-white tracking-tight">{title}</h3>
                <p className="mt-1 text-[11px] font-mono text-[#888] leading-relaxed">{body}</p>
              </div>
            </div>
            <div className="px-5 py-3 flex items-center justify-end gap-2">
              <button onClick={onCancel} disabled={busy}
                className="text-[10px] font-mono uppercase tracking-[0.2em] px-4 py-2 border border-[#222] text-[#aaa] hover:text-white hover:border-[#444] disabled:opacity-40"
              >Cancel</button>
              <button onClick={onConfirm} disabled={busy}
                className="text-[10px] font-mono uppercase tracking-[0.2em] px-4 py-2 border border-[rgba(232,139,160,0.55)] text-[#e88ba0] hover:bg-[rgba(232,139,160,0.08)] inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy && <Loader2 className="w-3 h-3 animate-spin" />}
                {confirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function QRCodePage() {
  // No tying to exhibition_id — QRs are a global utility.
  useOutletContext();

  // Form state
  const [form, setForm] = useState({
    title: '',
    description: '',
    destination_url: '',
    slug: '',
    is_dynamic: false,
    style_preset: 'minimal-print',
    with_logo: false,
  });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // List + trash
  const [items, setItems] = useState([]);
  const [trash, setTrash] = useState([]);
  const [showTrash, setShowTrash] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);

  // QR preview engine — singleton kept in a ref. Rebuilt on every
  // setting change via ``update()``; never recreated.
  const previewRef = useRef(null);
  const qrInstanceRef = useRef(null);
  // True when the current preset wants the barn-outline frame around
  // the QR. Read once per render so we can decorate the preview AND
  // the downloaded SVG/PNG identically.
  const isFramed = !!(PRESETS[form.style_preset] || {}).framed;
  const isInverted = !!(PRESETS[form.style_preset] || {}).inverted;

  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  // Compute the data that actually goes into the QR (dynamic vs static).
  const encodedData = useMemo(() => {
    if (form.is_dynamic && form.slug) {
      // Encode the public redirect URL; backend bumps scan_count then
      // 302s to destination_url. Editor can change the destination
      // later without reprinting the QR.
      return `${process.env.REACT_APP_BACKEND_URL}/api/q/${form.slug}`;
    }
    return form.destination_url;
  }, [form.is_dynamic, form.slug, form.destination_url]);

  // Boot the QR engine + first render.
  useEffect(() => {
    qrInstanceRef.current = new QRCodeStyling(
      buildQrOptions({
        data: encodedData,
        preset: form.style_preset,
        withLogo: form.with_logo,
        size: 1024,
      }),
    );
    if (previewRef.current) {
      previewRef.current.innerHTML = '';
      qrInstanceRef.current.append(previewRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update on every input change.
  useEffect(() => {
    if (!qrInstanceRef.current) return;
    qrInstanceRef.current.update(
      buildQrOptions({
        data: encodedData,
        preset: form.style_preset,
        withLogo: form.with_logo,
        size: 1024,
      }),
    );
  }, [encodedData, form.style_preset, form.with_logo]);

  // Fetch saved QRs.
  const fetchList = async () => {
    setLoading(true);
    try {
      const [active, trashed] = await Promise.allSettled([
        axios.get(`${API}/admin/qr-codes`),
        axios.get(`${API}/admin/qr-codes?deleted=true`),
      ]);
      const failed503 = (r) => r.status === 'rejected' && r.reason?.response?.status === 503;
      if (failed503(active) || failed503(trashed)) {
        setTableMissing(true);
        setItems([]); setTrash([]);
      } else {
        setTableMissing(false);
        setItems(active.status === 'fulfilled' ? active.value.data : []);
        setTrash(trashed.status === 'fulfilled' ? trashed.value.data : []);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchList(); }, []);

  // ─── Form actions ──────────────────────────────────────────────
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const resetForm = () => {
    setEditingId(null);
    setForm({
      title: '', description: '', destination_url: '', slug: '',
      is_dynamic: false, style_preset: 'minimal-print', with_logo: false,
    });
  };

  const loadIntoForm = (qr) => {
    setEditingId(qr.id);
    setForm({
      title: qr.title || '',
      description: qr.description || '',
      destination_url: qr.destination_url || '',
      slug: qr.slug || '',
      is_dynamic: !!qr.is_dynamic,
      style_preset: qr.style_preset || 'minimal-print',
      with_logo: !!(qr.style_config && qr.style_config.with_logo),
    });
    // Scroll the form back into view on smaller screens.
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const validUrl = isProbablyUrl(form.destination_url);
  const titleOk = form.title.trim().length > 0;
  const canSave = validUrl && titleOk;

  const saveQr = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        destination_url: form.destination_url.trim(),
        slug: form.slug.trim() || undefined,
        is_dynamic: form.is_dynamic,
        style_preset: form.style_preset,
        style_config: { with_logo: form.with_logo },
      };
      if (editingId) {
        const res = await axios.patch(`${API}/admin/qr-codes/${editingId}`, payload);
        toast.success(`Updated · ${res.data.title}`);
      } else {
        const res = await axios.post(`${API}/admin/qr-codes`, payload);
        toast.success(`Saved · ${res.data.title}`);
        if (res.data.slug && form.is_dynamic && form.slug !== res.data.slug) {
          // Slug was deduplicated server-side. Reflect in form.
          update({ slug: res.data.slug });
        }
      }
      await fetchList();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 503) setTableMissing(true);
      toast.error(detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ─── Downloads ─────────────────────────────────────────────────
  const safeName = useMemo(() => {
    const base = (form.title || form.slug || 'qr').toLowerCase().trim().replace(/[^a-z0-9-]+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');
    return base || 'qr';
  }, [form.title, form.slug]);

  // ─── Composite download ─────────────────────────────────────────
  // We don't use ``qr.download()`` directly because that exports only
  // the bare QR. To make the saved asset match what's on screen — and
  // in particular to bake the barn-outline FRAME into the export when
  // the "Framed" preset is active — we render a wrapper SVG ourselves
  // that contains the frame + the qr-code-styling SVG, then either
  // serve that SVG directly (for `.svg`) or rasterize it onto a canvas
  // (for `.png`). Same output for preview and download.
  //
  // Scan safety: the wrapper NEVER touches the QR module grid. The
  // frame sits in a generous outer margin; the QR's quiet zone is
  // preserved. Verified against scan-readers at 2cm × 2cm and up.
  const buildCompositeSvg = async (size = 1400) => {
    const qr = qrInstanceRef.current;
    if (!qr) return null;
    const svgBlob = await qr.getRawData('svg');
    if (!svgBlob) return null;
    const innerSvg = await svgBlob.text();
    const inner = innerSvg.replace(/^<\?xml[^?]*\?>\s*/i, '');
    const bg = isInverted ? '#000000' : '#FFFFFF';
    const stroke = isInverted ? '#FFFFFF' : '#000000';
    // Geometry — kept consistent with the live preview padding:
    //   Framed:   QR is a 56% square at (22%, 33%). That places it
    //             cleanly inside the barn body (walls at 14% / 86%,
    //             roofline at 27%, floor at 93%) with ~6% breathing
    //             room on every side.
    //   Unframed: QR fills the canvas with a 4% safety margin.
    const W = size;
    const qrX = Math.round(W * (isFramed ? 0.22 : 0.04));
    const qrY = Math.round(W * (isFramed ? 0.33 : 0.04));
    const qrSize = Math.round(W * (isFramed ? 0.56 : 0.92));
    const innerWithVB = inner.replace(
      /<svg([^>]*)>/i,
      `<svg$1 x="${qrX}" y="${qrY}" width="${qrSize}" height="${qrSize}" preserveAspectRatio="xMidYMid meet">`,
    );
    const frame = isFramed
      ? `
    <g transform="scale(${W / 100})" stroke="${stroke}" stroke-linejoin="miter" stroke-linecap="butt" fill="none">
      <path d="${BARN_PATH}" stroke-width="0.9" />
    </g>`
      : '';
    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${W}" width="${W}" height="${W}" shape-rendering="geometricPrecision">
  <rect width="${W}" height="${W}" fill="${bg}"/>
  ${frame}
  ${innerWithVB}
</svg>`;
  };

  const triggerDownload = (filename, blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // ─── PNG via canvas, NOT via SVG-in-img ─────────────────────────
  // Why we don't rasterize the composite SVG:
  // qr-code-styling embeds the optional TRG house mark as a nested
  // ``<image href="data:image/svg+xml;base64,…">``. When we then drop
  // that whole composite SVG into an ``<img>`` for canvas
  // rasterization, Chrome blocks the nested data-URL image (SVGs loaded
  // via <img> are treated as document-less, so they can't reference
  // sub-resources). That either fires ``onerror`` silently or taints
  // the canvas so ``toBlob`` returns null. Symptom: the SVG download
  // works, the PNG download does nothing.
  //
  // Robust fix: ask qr-code-styling for a **PNG** blob (it draws the
  // logo correctly into the bitmap itself), load THAT PNG into an
  // <img> — no nested SVG, no taint — and paint the barn frame on the
  // same canvas with native path commands.
  const buildCompositePng = async (size = 1400) => {
    const qr = qrInstanceRef.current;
    if (!qr) throw new Error('QR engine not ready');
    const bg = isInverted ? '#000000' : '#FFFFFF';
    const stroke = isInverted ? '#FFFFFF' : '#000000';
    // Same geometry as buildCompositeSvg.
    const W = size;
    const qrX = Math.round(W * (isFramed ? 0.22 : 0.04));
    const qrY = Math.round(W * (isFramed ? 0.33 : 0.04));
    const qrSize = Math.round(W * (isFramed ? 0.56 : 0.92));

    // 1) Get the bare QR as a PNG blob. The logo (if enabled) is
    //    already baked into these pixels by qr-code-styling.
    const qrBlob = await qr.getRawData('png');
    if (!qrBlob) throw new Error('qr-code-styling returned no PNG blob');

    // 2) Load it into an <img>. PNG carries no sub-resources, so
    //    Chrome will not refuse, and the canvas stays untainted.
    const qrUrl = URL.createObjectURL(qrBlob);
    const qrImg = new Image();
    // Don't set crossOrigin — qrUrl is a same-origin blob: URL, and
    // setting crossOrigin would force a CORS check on a non-CORS
    // resource (Firefox sometimes balks here).
    try {
      await new Promise((res, rej) => {
        qrImg.onload = res;
        qrImg.onerror = () => rej(new Error('Could not load QR PNG into image'));
        qrImg.src = qrUrl;
      });

      // 3) Compose on a 1400×1400 canvas.
      const canvas = document.createElement('canvas');
      canvas.width = W;
      canvas.height = W;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas 2D context unavailable');

      // Background.
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, W);

      // Barn-outline frame — drawn with canvas commands so no SVG is
      // ever loaded into the canvas. Path coordinates are the same
      // 0..100 BARN_PATH constants the preview uses, scaled to W.
      if (isFramed) {
        const s = W / 100;
        ctx.strokeStyle = stroke;
        ctx.lineWidth = 0.9 * s; // matches SVG export stroke
        ctx.lineJoin = 'miter';
        ctx.lineCap = 'butt';
        ctx.beginPath();
        ctx.moveTo(14 * s, 93 * s);
        ctx.lineTo(14 * s, 27 * s);
        ctx.lineTo(50 * s, 6 * s);
        ctx.lineTo(86 * s, 27 * s);
        ctx.lineTo(86 * s, 93 * s);
        ctx.closePath();
        ctx.stroke();
      }

      // Draw the QR last so the dots sit on top of the (transparent)
      // frame layer; the QR PNG carries its own background fill, so
      // the area inside the QR remains the correct color.
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

      // 4) Serialize.
      const pngBlob = await new Promise((resolve, reject) => {
        try {
          canvas.toBlob((b) => {
            if (b) resolve(b);
            else reject(new Error('canvas.toBlob returned null (canvas may be tainted)'));
          }, 'image/png');
        } catch (e) { reject(e); }
      });
      return pngBlob;
    } finally {
      URL.revokeObjectURL(qrUrl);
    }
  };

  const [downloading, setDownloading] = useState(null); // 'svg' | 'png' | null

  const download = async (ext) => {
    if (!qrInstanceRef.current) return;
    if (!validUrl) { toast.error('Enter a valid destination URL first'); return; }
    if (downloading) return;
    setDownloading(ext);
    try {
      if (ext === 'svg') {
        const svgText = await buildCompositeSvg(1400);
        if (!svgText) { toast.error('Could not render QR'); return; }
        triggerDownload(`${safeName}.svg`, new Blob([svgText], { type: 'image/svg+xml' }));
      } else if (ext === 'png') {
        const pngBlob = await buildCompositePng(1400);
        if (!pngBlob) { toast.error('Could not encode PNG'); return; }
        triggerDownload(`${safeName}.png`, pngBlob);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`QR ${ext} download failed:`, err);
      toast.error(`PNG export failed: ${err?.message || 'unknown error'}`);
    } finally {
      setDownloading(null);
    }
  };

  // ─── Trash actions ─────────────────────────────────────────────
  const askDelete = (qr) => setConfirm({ kind: 'soft', qr });
  const askPurge = (qr) => setConfirm({ kind: 'purge', qr });
  const performConfirm = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    try {
      if (confirm.kind === 'soft') {
        await axios.patch(`${API}/admin/qr-codes/${confirm.qr.id}/delete`);
        toast.success(`Moved to trash · ${confirm.qr.title}`);
      } else {
        await axios.delete(`${API}/admin/qr-codes/${confirm.qr.id}/permanent`);
        toast.success(`Permanently removed · ${confirm.qr.title}`);
      }
      if (editingId === confirm.qr.id) resetForm();
      await fetchList();
      setConfirm(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally {
      setConfirmBusy(false);
    }
  };
  const restoreQr = async (qr) => {
    try {
      await axios.patch(`${API}/admin/qr-codes/${qr.id}/restore`);
      toast.success(`Restored · ${qr.title}`);
      await fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    }
  };

  const copyDynamicUrl = async () => {
    if (!form.is_dynamic || !form.slug) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/q/${form.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Dynamic URL copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  const visibleList = showTrash ? trash : items;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-outfit text-white tracking-tight inline-flex items-center gap-2">
            <QrCode className="w-5 h-5 text-[#90E0EF]" />
            QR Code Generator
          </h1>
          <p className="text-[11px] font-mono text-[#777] mt-1">
            Artistic, brand-aligned QR codes for posters, brochures, gallery labels & invites.
          </p>
        </div>
        {tableMissing && (
          <div className="border border-[rgba(232,139,160,0.45)] bg-[rgba(232,139,160,0.06)] px-3 py-2 text-[10px] font-mono text-[#e88ba0] max-w-md">
            ⚠ The <code className="text-white">qr_codes</code> table is missing.
            Run the SQL in <code className="text-white">/app/memory/sql_handoff_qr_codes.md</code> to enable saving.
            Generation + download still work without it.
          </div>
        )}
      </div>

      {/* Two-column workspace */}
      <div className="grid grid-cols-12 gap-6">
        {/* ─── Left: form ─── */}
        <div className="col-span-12 lg:col-span-7 space-y-4">
          <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-5 space-y-4" data-testid="qr-form">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#777]">Title</label>
              <input
                value={form.title}
                onChange={(e) => update({ title: e.target.value })}
                placeholder="June Opening · Wall Label"
                data-testid="qr-title"
                className="mt-1 w-full bg-transparent border border-[#222] text-white text-xs font-mono px-3 py-2 focus:outline-none focus:border-[#90E0EF]"
              />
            </div>

            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#777]">Destination URL</label>
              <input
                value={form.destination_url}
                onChange={(e) => update({ destination_url: e.target.value })}
                placeholder="https://therelativegallery.com/opencall"
                data-testid="qr-destination"
                className={`mt-1 w-full bg-transparent border text-white text-xs font-mono px-3 py-2 focus:outline-none ${
                  form.destination_url && !validUrl
                    ? 'border-[#e88ba0] focus:border-[#e88ba0]'
                    : 'border-[#222] focus:border-[#90E0EF]'
                }`}
              />
              {form.destination_url && !validUrl && (
                <p className="text-[10px] font-mono text-[#e88ba0] mt-1">
                  That doesn't look like a valid URL. Use http(s) or a bare hostname.
                </p>
              )}
            </div>

            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#777]">Description (optional)</label>
              <input
                value={form.description}
                onChange={(e) => update({ description: e.target.value })}
                placeholder="Print on the back of the gallery label"
                className="mt-1 w-full bg-transparent border border-[#222] text-white text-xs font-mono px-3 py-2 focus:outline-none focus:border-[#90E0EF]"
              />
            </div>

            {/* Dynamic toggle */}
            <div className="border border-[#1a1a1a] p-3 bg-[#070707]">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.is_dynamic}
                  onChange={(e) => update({
                    is_dynamic: e.target.checked,
                    slug: e.target.checked && !form.slug
                      ? form.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 50)
                      : form.slug,
                  })}
                  data-testid="qr-is-dynamic"
                  className="mt-0.5 w-4 h-4 accent-[#90E0EF]"
                />
                <div className="min-w-0">
                  <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#90E0EF] inline-flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5" /> Dynamic QR
                  </div>
                  <div className="text-[10px] font-mono text-[#777] mt-1 leading-relaxed">
                    Encodes <code className="text-[#aaa]">/api/q/&lt;slug&gt;</code> instead of the URL directly.
                    You can change the destination later without reprinting. The backend bumps a scan counter on each visit.
                  </div>
                </div>
              </label>
              {form.is_dynamic && (
                <div className="mt-3 pl-7 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-[#555]">/api/q/</span>
                    <input
                      value={form.slug}
                      onChange={(e) => update({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-') })}
                      placeholder="june-opening"
                      data-testid="qr-slug"
                      className="flex-1 bg-transparent border border-[#222] text-white text-xs font-mono px-2 py-1.5 focus:outline-none focus:border-[#90E0EF]"
                    />
                    {form.slug && (
                      <button
                        type="button"
                        onClick={copyDynamicUrl}
                        title="Copy dynamic URL"
                        className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1.5 border border-[#222] text-[#aaa] hover:text-white hover:border-[#444] inline-flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  {form.slug && (
                    <div className="text-[10px] font-mono text-[#555] inline-flex items-center gap-1.5">
                      <Link2 className="w-3 h-3" />
                      {`${process.env.REACT_APP_BACKEND_URL}/api/q/${form.slug}`}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Presets */}
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#777]">Preset</label>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(PRESETS).map(([key, p]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => update({ style_preset: key })}
                    data-testid={`qr-preset-${key}`}
                    className={`text-left p-3 border transition-all ${
                      form.style_preset === key
                        ? 'border-[#90E0EF] bg-[rgba(144,224,239,0.06)]'
                        : 'border-[#222] hover:border-[#444]'
                    }`}
                  >
                    <div className={`text-[11px] font-mono uppercase tracking-[0.18em] ${
                      form.style_preset === key ? 'text-[#90E0EF]' : 'text-white'
                    }`}>{p.label}</div>
                    <div className="text-[10px] font-mono text-[#666] mt-1 leading-snug">{p.helper}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Logo toggle */}
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.with_logo}
                onChange={(e) => update({ with_logo: e.target.checked })}
                data-testid="qr-with-logo"
                className="w-4 h-4 accent-[#90E0EF]"
              />
              <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#aaa]">
                Centered TRG mark (uses High error-correction so it stays scannable)
              </span>
            </label>

            {/* Buttons */}
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#1a1a1a]">
              <button
                onClick={() => download('png')}
                disabled={!validUrl || downloading === 'png'}
                data-testid="qr-download-png"
                className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] px-4 py-2 border border-[#222] text-[#aaa] hover:text-white hover:border-[#444] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {downloading === 'png'
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <FileImage className="w-3.5 h-3.5" />}
                {downloading === 'png' ? 'Rendering…' : 'Download PNG'}
              </button>
              <button
                onClick={() => download('svg')}
                disabled={!validUrl || downloading === 'svg'}
                data-testid="qr-download-svg"
                className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] px-4 py-2 border border-[#222] text-[#aaa] hover:text-white hover:border-[#444] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {downloading === 'svg'
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <FileCode className="w-3.5 h-3.5" />}
                {downloading === 'svg' ? 'Rendering…' : 'Download SVG'}
              </button>
              <div className="ml-auto flex items-center gap-2">
                {editingId && (
                  <button
                    onClick={resetForm}
                    className="text-[10px] font-mono uppercase tracking-[0.2em] px-3 py-2 border border-[#222] text-[#aaa] hover:text-white hover:border-[#444]"
                  >
                    Clear
                  </button>
                )}
                <button
                  onClick={saveQr}
                  disabled={!canSave || saving}
                  data-testid="qr-save"
                  className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] px-5 py-2 bg-[#90E0EF] text-black hover:bg-[#a8e8f4] shadow-[0_0_18px_rgba(144,224,239,0.3)] disabled:bg-[#1a1a1a] disabled:text-[#555] disabled:shadow-none disabled:cursor-not-allowed"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  {editingId ? 'Update QR' : 'Save QR'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Right: live preview ─── */}
        <div className="col-span-12 lg:col-span-5 space-y-4">
          <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-5">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#777] mb-3">Live preview</div>
            <div
              className={`flex items-center justify-center p-6 border border-[#1a1a1a] aspect-square overflow-hidden ${
                isInverted ? 'bg-black' : 'bg-white'
              }`}
            >
              {/* Composite stage — same geometry as the export */}
              <div
                className="relative w-full max-w-[360px] aspect-square flex items-center justify-center"
                style={{ filter: validUrl ? 'none' : 'opacity(0.35) blur(0.5px)' }}
                data-testid="qr-stage"
              >
                {/* Barn-outline frame · drawn behind the QR, sits in
                    the outer margin so it never overlaps the QR
                    quiet zone. Pure stroke, no fill. */}
                {isFramed && (
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="xMidYMid meet"
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    data-testid="qr-frame"
                  >
                    <path
                      d={BARN_PATH}
                      fill="none"
                      stroke={isInverted ? '#FFFFFF' : '#000000'}
                      strokeWidth="0.9"
                      strokeLinejoin="miter"
                      strokeLinecap="butt"
                      vectorEffect="non-scaling-stroke"
                    />
                  </svg>
                )}
                <div
                  ref={previewRef}
                  data-testid="qr-preview"
                  className="relative w-full h-full flex items-center justify-center"
                  style={isFramed ? { padding: '33% 22% 11% 22%' } : undefined}
                />
              </div>
            </div>
            <div className="mt-3 space-y-1.5">
              <div className="text-[10px] font-mono text-[#777]">
                Encoded: <span className="text-[#aaa] break-all">{encodedData || '—'}</span>
              </div>
              {form.is_dynamic && form.slug && (
                <div className="text-[10px] font-mono text-[#90E0EF] inline-flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" /> Dynamic · redirect target editable later
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Saved list / Trash toggle ─── */}
      <div className="border border-[#1a1a1a] bg-[#0a0a0a]">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-[#1a1a1a]">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-white inline-flex items-center gap-2">
            {showTrash ? <Trash2 className="w-3.5 h-3.5 text-[#e88ba0]" /> : <QrCode className="w-3.5 h-3.5 text-[#90E0EF]" />}
            {showTrash ? `Trash · ${trash.length}` : `Saved · ${items.length}`}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowTrash(false)}
              data-testid="qr-tab-active"
              className={`text-[10px] font-mono uppercase tracking-[0.2em] px-3 py-1.5 border ${
                !showTrash ? 'border-[#90E0EF] text-[#90E0EF]' : 'border-[#222] text-[#777] hover:text-white hover:border-[#444]'
              }`}
            >Active · {items.length}</button>
            <button
              type="button"
              onClick={() => setShowTrash(true)}
              data-testid="qr-tab-trash"
              className={`text-[10px] font-mono uppercase tracking-[0.2em] px-3 py-1.5 border ${
                showTrash ? 'border-[#e88ba0] text-[#e88ba0]' : 'border-[#222] text-[#777] hover:text-white hover:border-[#444]'
              }`}
            >Trash · {trash.length}</button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-[#666] font-mono text-xs">Loading…</div>
        ) : visibleList.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-[#666] font-mono text-sm">
              {showTrash ? 'Trash is empty' : 'No saved QR codes yet'}
            </p>
            <p className="text-[10px] font-mono text-[#444] mt-2">
              {showTrash ? 'Soft-deleted QRs land here for recovery.' : 'Fill the form above and hit “Save QR”.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="border-b border-[#1a1a1a] text-[#666] uppercase tracking-[0.15em]">
                  <th className="text-left p-3">Title</th>
                  <th className="text-left p-3">Destination</th>
                  <th className="text-left p-3">Style</th>
                  <th className="text-left p-3">Scans</th>
                  <th className="text-right p-3 w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleList.map((q) => (
                  <tr key={q.id} className="group border-b border-[#111] hover:bg-[#111] transition-colors">
                    <td className="p-3 text-white">
                      <div>{q.title}</div>
                      {q.is_dynamic && q.slug && (
                        <div className="text-[10px] text-[#90E0EF] mt-0.5">/q/{q.slug}</div>
                      )}
                    </td>
                    <td className="p-3 text-[#aaa] max-w-[260px] truncate">{q.destination_url}</td>
                    <td className="p-3 text-[#888]">{(PRESETS[q.style_preset] || {}).label || q.style_preset}</td>
                    <td className="p-3 text-[#888]">{q.scan_count || 0}</td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        {showTrash ? (
                          <>
                            <button
                              type="button"
                              onClick={() => restoreQr(q)}
                              title="Restore"
                              className="opacity-80 group-hover:opacity-100 text-[#7ed4a0] hover:text-[#9ae0b5] p-1.5 border border-[rgba(126,212,160,0.4)] hover:bg-[rgba(126,212,160,0.06)]"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => askPurge(q)}
                              title="Delete permanently"
                              className="opacity-80 group-hover:opacity-100 text-[#e88ba0] hover:text-[#f0a5b9] p-1.5 border border-[rgba(232,139,160,0.4)] hover:bg-[rgba(232,139,160,0.06)]"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => loadIntoForm(q)}
                              title="Edit"
                              className="opacity-60 group-hover:opacity-100 text-[#777] hover:text-[#90E0EF] p-1.5 border border-transparent hover:border-[rgba(144,224,239,0.4)]"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => askDelete(q)}
                              title="Move to trash"
                              className="opacity-60 group-hover:opacity-100 text-[#777] hover:text-[#e88ba0] p-1.5 border border-transparent hover:border-[rgba(232,139,160,0.4)]"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!confirm}
        title={confirm?.kind === 'purge' ? 'Delete this QR permanently?' : 'Move this QR to trash?'}
        body={
          confirm?.kind === 'purge'
            ? `“${confirm?.qr?.title}” will be wiped permanently. If it was a dynamic QR, any printed copies will return 404 from now on.`
            : `“${confirm?.qr?.title}” will move to the Trash tab. You can restore or permanently delete it from there.`
        }
        confirmLabel={confirm?.kind === 'purge' ? 'Yes, delete permanently' : 'Yes, move to trash'}
        busy={confirmBusy}
        onConfirm={performConfirm}
        onCancel={() => (confirmBusy ? null : setConfirm(null))}
      />
    </div>
  );
}
